import * as React from "react"
import { Head, router, usePage } from "@inertiajs/react"
import { ChevronDown, ChevronRight, ExternalLink, X } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ThreadWorkspace } from "@/components/inbox/ThreadWorkspace"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"
import type { SelectedReply } from "@/components/inbox/types"

type TodayRow = {
  readonly id: number
  readonly prospect: string
  readonly company: string | null
  readonly client_name: string | null
  readonly client_color: string | null
  readonly owner_id: number | null
  readonly owner_label: string | null
  readonly bucket: string
  readonly timing: string
  readonly stage_label: string | null
  readonly draft_state: "ready" | "drafting" | "none"
  readonly subject: string | null
  readonly snippet: string | null
  readonly inbox_url: string
}

type UserOption = {
  readonly id: number
  readonly label: string
  readonly email: string
}

type TodayProps = {
  readonly waiting: readonly TodayRow[]
  readonly due: readonly TodayRow[]
  readonly upcoming: readonly TodayRow[]
  readonly no_next_step: readonly TodayRow[]
  readonly counts: {
    readonly waiting: number
    readonly due: number
    readonly upcoming: number
    readonly no_next_step: number
  }
  readonly truncated: {
    readonly waiting: boolean
    readonly due: boolean
    readonly upcoming: boolean
    readonly no_next_step: boolean
  }
  readonly row_limit: number
  readonly filters: { readonly scope: string }
  readonly users: readonly UserOption[]
  readonly selected_reply: SelectedReply | null
  readonly clients: readonly { readonly id: number; readonly display_name: string }[]
  readonly classifications: readonly string[]
}

export default function TodayPage({
  waiting,
  due,
  upcoming,
  no_next_step,
  counts,
  truncated,
  row_limit,
  filters,
  users,
  selected_reply,
  clients,
  classifications,
}: TodayProps) {
  const { props } = usePage<PageProps<TodayProps>>()
  const description = "Today's priority queue: replies waiting on you, follow-ups due, and upcoming."

  const scope = filters.scope

  const setScope = (newScope: string) => {
    router.get("/today", { scope: newScope }, { preserveState: true, preserveScroll: true })
  }

  const openReply = (id: number) => {
    router.get(
      "/today",
      { scope, selected: id },
      { only: ["selected_reply"], preserveState: true, preserveScroll: true },
    )
  }

  const closePanel = () => {
    router.get(
      "/today",
      { scope },
      { only: ["selected_reply"], preserveState: true, preserveScroll: true },
    )
  }

  const [openSections, setOpenSections] = React.useState({ upcoming: false, no_next_step: false })
  const [focusedId, setFocusedId] = React.useState<number | null>(null)
  const rowRefMap = React.useRef(new Map<number, HTMLElement>())

  const setRowRef = React.useCallback((id: number, el: HTMLElement | null) => {
    if (el) {
      rowRefMap.current.set(id, el)
    } else {
      rowRefMap.current.delete(id)
    }
  }, [])

  const navigableRows = React.useMemo(() => [
    ...waiting,
    ...due,
    ...(openSections.upcoming ? upcoming : []),
    ...(openSections.no_next_step ? no_next_step : []),
  ], [waiting, due, upcoming, no_next_step, openSections])

  const selectedId = selected_reply?.id ?? null

  React.useEffect(() => {
    if (focusedId !== null) {
      const exists = navigableRows.some(r => r.id === focusedId)
      if (!exists) {
        setFocusedId(navigableRows[0]?.id ?? null)
      }
    }
  }, [navigableRows, focusedId])

  React.useEffect(() => {
    if (focusedId !== null) {
      const el = rowRefMap.current.get(focusedId)
      if (el) {
        el.scrollIntoView({ block: "nearest" })
      }
    }
  }, [focusedId])

  // Follow an in-flight redraft to completion. The worker installs the new draft
  // asynchronously, seconds after the request returns; without this the panel
  // keeps showing (and Send keeps posting) the draft that is being replaced.
  // The Inbox gets this from its ActionCable/10s poll — Today has neither, so it
  // polls only while a job is actually running rather than on a blanket timer.
  const draftingStatus = selected_reply?.drafting_job?.status ?? null

  React.useEffect(() => {
    if (typeof window === "undefined") return
    if (!selected_reply) return
    if (!draftingStatus || !["queued", "claimed", "drafting"].includes(draftingStatus)) return

    const timer = window.setInterval(() => {
      router.reload({ only: ["selected_reply"] })
    }, 3000)
    return () => window.clearInterval(timer)
  }, [selected_reply?.id, draftingStatus])

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        if (!selected_reply) return

        // Send-from-the-editor is the point of this binding, so contenteditable
        // is allowed — but NOT plain fields. The redraft "Revision instructions"
        // input treats a bare Enter as Regenerate, so Ctrl+Enter there would
        // queue a redraft AND click Send, dispatching the draft being replaced.
        const sendTarget = event.target as HTMLElement | null
        const sendTag = sendTarget?.tagName
        if (sendTag === "INPUT" || sendTag === "TEXTAREA" || sendTag === "SELECT") return

        event.preventDefault()
        document.querySelector<HTMLButtonElement>("[data-send-button]")?.click()
        return
      }

      if (event.metaKey || event.ctrlKey || event.altKey) return
      const el = event.target as HTMLElement | null
      if (!el) return
      const tag = el.tagName
      const isTextInput = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT"
      const isContentEditable = el.isContentEditable
      const isInEditor = el.closest(
        "[contenteditable='true'], .ProseMirror, .milkdown, [role='textbox'], [role='dialog'], [role='menu']",
      )

      if (event.key === "Escape") {
        // Escape belongs to the editor first. Closing the slide-over out from
        // under an in-progress draft would discard unsaved text; inside the
        // editor Esc should collapse a selection or dismiss an editor popup.
        // Close the panel with the X, the backdrop, or Esc from outside a field.
        if (isTextInput || isContentEditable || isInEditor) return
        if (el.closest("[role='dialog'], [role='menu']")) return
        if (selected_reply) {
          event.preventDefault()
          closePanel()
        } else {
          event.preventDefault()
          setFocusedId(null)
        }
        return
      }

      if (isTextInput || isContentEditable || isInEditor) return

      if (event.key === "e" && selected_reply) {
        event.preventDefault()
        router.post(`/replies/${selected_reply.id}/archive`, {}, { preserveScroll: true })
        return
      }

      if (event.key === "s" && selected_reply) {
        event.preventDefault()
        router.post(`/replies/${selected_reply.id}/snooze`, { hours: 24 }, { preserveScroll: true })
        return
      }

      if (event.key === "r" && selected_reply) {
        event.preventDefault()
        router.post(`/replies/${selected_reply.id}/regenerate`, {}, { preserveScroll: true })
        return
      }

      if (event.key === "j" || event.key === "ArrowDown") {
        event.preventDefault()
        if (navigableRows.length === 0) return

        if (focusedId === null) {
          const firstId = navigableRows[0].id
          setFocusedId(firstId)
          return
        }

        const idx = navigableRows.findIndex(r => r.id === focusedId)
        if (idx === -1) {
          const firstId = navigableRows[0].id
          setFocusedId(firstId)
          return
        }
        if (idx >= navigableRows.length - 1) return

        const newId = navigableRows[idx + 1].id
        setFocusedId(newId)
        return
      }

      if (event.key === "k" || event.key === "ArrowUp") {
        event.preventDefault()
        if (navigableRows.length === 0) return

        if (focusedId === null) {
          const firstId = navigableRows[0].id
          setFocusedId(firstId)
          return
        }

        const idx = navigableRows.findIndex(r => r.id === focusedId)
        if (idx === -1) {
          const firstId = navigableRows[0].id
          setFocusedId(firstId)
          return
        }
        if (idx <= 0) return

        const newId = navigableRows[idx - 1].id
        setFocusedId(newId)
        return
      }

      if (event.key === "Enter") {
        // Enter belongs to whatever is focused. Without this, tabbing to a
        // control inside the panel (e.g. the follow-up date "Save" button) and
        // pressing Enter would open a thread instead of activating it.
        if (tag === "BUTTON" || tag === "A" || el.closest("[role='button']")) return

        event.preventDefault()
        if (focusedId !== null) {
          openReply(focusedId)
        }
        return
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [navigableRows, focusedId, selected_reply?.id, scope])

  // Panel-follow, as a real debounce: while the panel is open, moving the
  // cursor re-points it at the focused row. Each focusedId change cancels the
  // previous pending open, so holding j fires one request, not one per row.
  React.useEffect(() => {
    if (!selected_reply || focusedId === null) return
    if (focusedId === selected_reply.id) return

    const timer = setTimeout(() => openReply(focusedId), 180)
    return () => clearTimeout(timer)
  }, [focusedId, selected_reply?.id, scope])

  const handleRowClick = (id: number) => {
    setFocusedId(id)
    openReply(id)
  }

  const filterChips: { readonly scope: string; readonly label: string }[] = [
    { scope: "mine", label: "Mine" },
    { scope: "unassigned", label: "Unassigned" },
    { scope: "all", label: "Everything" },
  ]

  return (
    <>
      <Head title="Today">
        <meta name="description" content={description} />
        <meta property="og:title" content="Today" />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell wide>
        <div className="flex flex-col gap-8">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1>Today</h1>
              <p className="mt-2">Replies waiting on you, follow-ups due today, and upcoming.</p>
              <p className="mt-1 text-xs text-ink-muted">j / k move · enter open · e archive · s snooze · r redraft · esc close</p>
            </div>
            <div className="flex gap-2">
              {filterChips.map((chip) => (
                <button
                  key={chip.scope}
                  type="button"
                  onClick={() => setScope(chip.scope)}
                  className={cn(
                    "inline-flex cursor-pointer items-center rounded-full px-3 py-1 text-[13px] font-medium border transition-colors",
                    scope === chip.scope
                      ? "border-accent bg-accent-faded text-accent-display"
                      : "border-hairline bg-page text-ink-muted hover:bg-surface hover:text-ink-display",
                  )}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          </header>

          {props.flash.notice && (
            <div className="rounded-md border border-accent bg-accent-faded px-4 py-3 text-sm text-accent-display">
              {props.flash.notice}
            </div>
          )}

          <section className="flex flex-col gap-4">
            <h2 className="text-danger-display">WAITING ON US · {counts.waiting}</h2>
            {waiting.length === 0 ? (
              <p className="text-sm text-ink-muted">Nobody is waiting on you.</p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-hairline bg-page border-amber-600/30">
                <div className="divide-y divide-hairline">
                  {waiting.map((row) => (
                    <TodayRow
                      key={row.id}
                      row={row}
                      users={users}
                      selectedId={selectedId}
                      focusedId={focusedId}
                      registerRef={setRowRef}
                      onSelect={handleRowClick}
                    />
                  ))}
                </div>
              </div>
            )}
            <TruncationNotice shown={truncated.waiting} limit={row_limit} total={counts.waiting} />
          </section>

          <section className="flex flex-col gap-4">
            <h2 className="text-amber-600">FOLLOW-UP DUE · {counts.due}</h2>
            {due.length === 0 ? (
              <p className="text-sm text-ink-muted">Nothing due today.</p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-hairline bg-page border-amber-600/30">
                <div className="divide-y divide-hairline">
                  {due.map((row) => (
                    <TodayRow
                      key={row.id}
                      row={row}
                      users={users}
                      selectedId={selectedId}
                      focusedId={focusedId}
                      registerRef={setRowRef}
                      onSelect={handleRowClick}
                    />
                  ))}
                </div>
              </div>
            )}
            <TruncationNotice shown={truncated.due} limit={row_limit} total={counts.due} />
          </section>

          {upcoming.length > 0 && (
            <CollapsibleSection
              title={`UPCOMING (${counts.upcoming})`}
              open={openSections.upcoming}
              onToggle={() => setOpenSections(s => ({ ...s, upcoming: !s.upcoming }))}
            >
              <div className="overflow-hidden rounded-lg border border-hairline bg-page">
                <div className="divide-y divide-hairline">
                  {upcoming.map((row) => (
                    <TodayRow
                      key={row.id}
                      row={row}
                      users={users}
                      selectedId={selectedId}
                      focusedId={focusedId}
                      registerRef={setRowRef}
                      onSelect={handleRowClick}
                    />
                  ))}
                </div>
              </div>
              <TruncationNotice shown={truncated.upcoming} limit={row_limit} total={counts.upcoming} />
            </CollapsibleSection>
          )}

          {no_next_step.length > 0 && (
            <CollapsibleSection
              title={`NO NEXT STEP (${counts.no_next_step})`}
              open={openSections.no_next_step}
              onToggle={() => setOpenSections(s => ({ ...s, no_next_step: !s.no_next_step }))}
            >
              <p className="mb-3 text-sm text-ink-muted">
                You answered these and nothing is scheduled. They are not overdue &mdash; they are
                simply parked without a next action. Open one to set a follow-up date or close it out.
              </p>
              <div className="overflow-hidden rounded-lg border border-hairline bg-page">
                <div className="divide-y divide-hairline">
                  {no_next_step.map((row) => (
                    <TodayRow
                      key={row.id}
                      row={row}
                      users={users}
                      selectedId={selectedId}
                      focusedId={focusedId}
                      registerRef={setRowRef}
                      onSelect={handleRowClick}
                    />
                  ))}
                </div>
              </div>
              <TruncationNotice shown={truncated.no_next_step} limit={row_limit} total={counts.no_next_step} />
            </CollapsibleSection>
          )}
        </div>
      </AppShell>

      {selected_reply && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/20"
            onClick={closePanel}
            aria-hidden="true"
          />
          <div className="fixed right-0 top-0 z-50 h-full w-full max-w-[780px] border-l border-hairline bg-page shadow-xl flex flex-col">
            <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-hairline bg-page px-4 py-3">
              <div className="min-w-0">
                <h3 className="truncate">{selected_reply.lead_name}</h3>
                {selected_reply.lead_email && (
                  <p className="text-xs text-ink-muted">{selected_reply.lead_email}</p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <a
                  href={selected_reply.id ? `/inbox?selected=${selected_reply.id}` : "/inbox"}
                  className="inline-flex items-center gap-1 rounded-md border border-hairline px-2.5 py-1 text-xs text-ink-muted no-underline hover:bg-surface hover:text-ink-display transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Inbox
                </a>
                <button
                  type="button"
                  className="flex shrink-0 cursor-pointer items-center justify-center rounded-md border border-hairline p-1.5 text-ink-muted hover:bg-surface hover:text-ink-display transition-colors"
                  onClick={closePanel}
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </header>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <ThreadWorkspace reply={selected_reply} clients={clients} classifications={classifications} />
            </div>
          </div>
        </>
      )}
    </>
  )
}

function TodayRow({
  row,
  users,
  selectedId,
  focusedId,
  registerRef,
  onSelect,
}: {
  readonly row: TodayRow
  readonly users: readonly UserOption[]
  readonly selectedId: number | null
  readonly focusedId: number | null
  readonly registerRef: (id: number, el: HTMLElement | null) => void
  readonly onSelect: (id: number) => void
}) {
  return (
    <div
      ref={(el) => registerRef(row.id, el)}
      className={cn(
        "grid cursor-pointer grid-cols-[auto_1fr] items-center gap-2 px-3 py-2 text-[13px] hover:bg-surface border-l-[3px]",
        selectedId === row.id ? "border-l-accent bg-surface" : "border-l-transparent",
        focusedId === row.id && "ring-1 ring-accent ring-inset",
      )}
      onClick={() => onSelect(row.id)}
    >
      <OwnerChip ownerId={row.owner_id} ownerLabel={row.owner_label} replyId={row.id} users={users} />
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="font-medium text-ink-display truncate">{row.prospect}</span>
        <span className="text-ink-muted select-none">·</span>
        <span className="text-ink-muted truncate">{row.company || "--"}</span>
        <span className="text-ink-muted select-none">·</span>
        {row.client_name ? (
          <span className="inline-flex items-center gap-1">
            <span
              style={{ backgroundColor: row.client_color ?? undefined }}
              className="inline-block h-2 w-2 shrink-0 rounded-full"
            />
            <span className="truncate text-ink-muted">{row.client_name}</span>
          </span>
        ) : (
          <span className="text-ink-muted">--</span>
        )}
        <span className="text-ink-muted select-none">·</span>
        <span className="text-ink-muted shrink-0">{row.timing}</span>
        <span className="text-ink-muted select-none">·</span>
        <span className="text-ink-muted shrink-0">{row.stage_label || "--"}</span>
        <span className="text-ink-muted select-none">·</span>
        <span className="text-ink-muted shrink-0">{draftStateLabel(row.draft_state)}</span>
      </div>
    </div>
  )
}

function OwnerChip({
  ownerId: _ownerId,
  ownerLabel,
  replyId,
  users,
}: {
  readonly ownerId: number | null
  readonly ownerLabel: string | null
  readonly replyId: number
  readonly users: readonly UserOption[]
}) {
  const [open, setOpen] = React.useState(false)

  const assign = (userId: string) => {
    router.post(
      `/replies/${replyId}/assign`,
      { user_id: userId },
      { preserveScroll: true },
    )
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex shrink-0 cursor-pointer items-center justify-center"
          onClick={(e) => e.stopPropagation()}
          aria-label={ownerLabel ? `Assigned to ${ownerLabel}` : "Unassigned"}
        >
          {ownerLabel ? (
            <Badge tone="neutral" className="h-5 w-5 rounded-full p-0 text-[10px] flex items-center justify-center">
              {ownerLabel}
            </Badge>
          ) : (
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-hairline text-[10px] text-ink-muted">
              --
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-48"
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenuLabel>Assign to</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => assign("me")}>
          Claim (me)
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {users.map((user) => (
          <DropdownMenuItem key={user.id} onSelect={() => assign(String(user.id))}>
            {user.label} · {user.email}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => assign("none")}>
          Unassign
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}


function draftStateLabel(state: "ready" | "drafting" | "none"): string {
  switch (state) {
    case "ready": return "ready"
    case "drafting": return "drafting"
    default: return "--"
  }
}

function CollapsibleSection({
  title,
  open,
  onToggle,
  children,
}: {
  readonly title: string
  readonly open: boolean
  readonly onToggle: () => void
  readonly children: React.ReactNode
}) {
  const icon = open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />

  return (
    <section className="flex flex-col gap-4">
      <button
        type="button"
        className="flex items-center gap-2 cursor-pointer opacity-60 hover:opacity-100 transition-opacity"
        onClick={onToggle}
      >
        {icon}
        <h2 className="text-ink-muted">{title}</h2>
      </button>
      {open && <div>{children}</div>}
    </section>
  )
}

function TruncationNotice({
  shown,
  limit,
  total,
}: {
  readonly shown: boolean
  readonly limit: number
  readonly total: number
}) {
  if (!shown) return null
  return (
    <p className="text-[13px] text-ink-muted">
      Showing the oldest {limit} of {total}. Clear some to see the rest.
    </p>
  )
}
